/* BattleCard — premium creature card. The player's wildlife photograph
 * is the hero. Adds rarity border, gentle idle tilt, glow, and an
 * `impact` flag that briefly shakes + scales the card when it's hit
 * or attacks. The photograph itself is NEVER replaced or generated.
 *
 * PERF: memoised so the card doesn't re-render on unrelated parent
 * state changes (HP ticks, floater pushes, awaiting state). Idle tilt
 * uses a plain CSS keyframe animation — no framer-motion loop. Impact
 * uses transform-only animations (no `filter: brightness()` which
 * spikes mobile GPU).
 */
import { memo } from 'react';
import { motion } from 'framer-motion';
import { RARITY_UI } from '../../lib/rarity';

const IMPACT_ANIM = {
  hit: {
    x: [0, -8, 6, -4, 0],
    transition: { duration: 0.28, ease: 'easeOut' },
  },
  attack: {
    scale: [1, 1.06, 1],
    transition: { duration: 0.24 },
  },
  defeated: {
    opacity: [1, 0.55],
    scale: [1, 0.97],
    transition: { duration: 0.7 },
  },
};

function frameStyle(frame) {
  const x = frame?.x ?? 50;
  const y = frame?.y ?? 50;
  const scale = (frame?.scale ?? 100) / 100;
  return {
    objectPosition: `${x}% ${y}%`,
    transform: `scale(${scale})`,
  };
}

function BattleCard({
  side,                 // 'challenger' | 'opponent' — for layout mirroring
  snapshot,             // {name, rarity, picture, frame, species_category}
  serial,               // "#0042"
  isActive = false,
  impactState = null,   // 'hit' | 'attack' | 'defeated' | null
  size = 'lg',          // 'lg' | 'md'
}) {
  const rarity = snapshot?.rarity || 'common';
  const ui = RARITY_UI[rarity] || RARITY_UI.common;
  const glow = ui.color || '#5FA9E5';
  const dims = size === 'lg' ? 'w-44 aspect-[4/5]' : 'w-28 aspect-[4/5]';
  const tiltClass = side === 'challenger' ? 'wq-card-tilt-left' : 'wq-card-tilt-right';

  return (
    <motion.div
      animate={impactState ? IMPACT_ANIM[impactState] : { x: 0, scale: 1, opacity: 1 }}
      data-testid={`battle-card-${side}`}
      className={`relative ${dims} rounded-3xl overflow-hidden shrink-0`}
      style={{
        border: `2px solid ${glow}`,
        boxShadow: isActive
          ? `0 0 0 3px ${glow}44, 0 0 48px -8px ${glow}bb, inset 0 0 40px -12px ${glow}77`
          : `0 0 24px -8px ${glow}77`,
      }}
    >
      {/* Idle tilt — CSS keyframe (see index.css .wq-card-tilt-*) so
          it stays smooth without a JS animation loop per card. */}
      <div className={`absolute inset-0 ${tiltClass}`}>
        {snapshot?.picture ? (
          <img
            src={snapshot.picture}
            alt=""
            style={frameStyle(snapshot.frame)}
            className="w-full h-full object-cover origin-center"
            loading="eager"
            decoding="async"
            onError={(e) => { e.currentTarget.style.opacity = '0.25'; }}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-emerald-700 to-teal-950 flex items-center justify-center">
            <span className="font-serif text-white/20 text-5xl">?</span>
          </div>
        )}
      </div>

      {/* Bottom nameplate */}
      <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/90 via-black/60 to-transparent">
        <div className="flex items-center justify-between gap-1 mb-0.5">
          <span
            className="text-[9px] uppercase tracking-widest font-bold"
            style={{ color: glow }}
          >
            {ui.label || rarity}
          </span>
          {serial && (
            <span className="text-[9px] font-mono text-white/50 tabular-nums">{serial}</span>
          )}
        </div>
        <p
          className="font-serif leading-tight text-white drop-shadow-lg"
          style={{ fontSize: size === 'lg' ? '1.05rem' : '0.85rem' }}
        >
          {snapshot?.name || 'Unknown'}
        </p>
      </div>

      {/* Defeated overlay */}
      {impactState === 'defeated' && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          <p className="font-serif text-white/70 text-xs uppercase tracking-widest">Defeated</p>
        </div>
      )}
    </motion.div>
  );
}

export default memo(BattleCard);
